import {
  memo, useCallback, useEffect, useRef, useState,
} from 'react';
import classNames from 'classnames';
import { useGlobalContext } from '~/context/GlobalContext';
import { useViewportResize } from '~/hooks/useViewportResize';
import { IMessage } from '~/types/common';
import Message from '~/components/MessageContainer/Message';
import emoticonsData from '~/data/emoticons.json';
import gifsData from '~/data/gifs.json';
import styles from './styles/RouletteChat.module.css';

const ITEMS_PER_PAGE = 50;

type PickerType = 'em' | 'gif';

interface PickerItem { id: number; url: string }

// ── Lista emotek/gifów z leniwym doładowywaniem ───────────────────────────
const PaginatedList = memo(({ items, type, basePath, onPick, active }: {
  items: PickerItem[];
  type: PickerType;
  basePath: string;
  onPick: (id: number, type: PickerType) => void;
  active: boolean;
}) => {
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const loaderRef = useRef<HTMLDivElement>(null);

  const loadMore = useCallback(() => {
    setVisibleCount(prev => Math.min(prev + ITEMS_PER_PAGE, items.length));
  }, [items.length]);

  useEffect(() => {
    if (!active || !loaderRef.current) return;
    const observer = new IntersectionObserver(
      entries => { if (entries[0].isIntersecting) loadMore(); },
      { threshold: 0.1 },
    );
    observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [active, loadMore, visibleCount]);

  return (
    <div className={styles.pickerGroup}>
      {items.slice(0, visibleCount).map(item => (
        <button
          key={item.id}
          type="button"
          className={styles.pickerItem}
          onMouseDown={e => { e.preventDefault(); onPick(item.id, type); }}
        >
          <img
            src={`${basePath}${item.url}`}
            alt=""
            loading="lazy"
            className={type === 'em' ? styles.pickerEmote : styles.pickerGif}
          />
        </button>
      ))}
      {visibleCount < items.length && (
        <div ref={loaderRef} className={styles.pickerLoader} />
      )}
    </div>
  );
});

// ── Czat ruletki (samodzielny, theme-aware, RWD) ──────────────────────────
const RouletteChat = () => {
  const { context } = useGlobalContext();
  const resize = useViewportResize();

  const messages = context.room?.messages ?? [];
  const roomId = context.room?.id ?? '';

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [pickerTab, setPickerTab] = useState<'emoticons' | 'gifs'>('emoticons');

  // Auto-scroll do najnowszej wiadomości
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, resize.isKeyboardOpen]);

  // Dopasowanie wysokości pola tekstowego
  const resizeInput = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;
    input.style.height = 'auto';
    const maxH = 120;
    input.style.height = `${Math.min(input.scrollHeight, maxH)}px`;
    input.style.overflowY = input.scrollHeight > maxH ? 'auto' : 'hidden';
  }, []);

  // Wstawienie tekstu w aktualnej pozycji kursora
  const insertAtCursor = useCallback((text: string) => {
    const input = inputRef.current;
    if (!input) return;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    input.value = input.value.slice(0, start) + text + input.value.slice(end);
    const caret = start + text.length;
    input.setSelectionRange(caret, caret);
    input.focus();
    resizeInput();
  }, [resizeInput]);

  // Odpisywanie na wiadomość (@nick) — spójne z głównym czatem
  useEffect(() => {
    const handleReply = (e: Event) => {
      const message = (e as CustomEvent<IMessage>).detail;
      if (!message?.userName) return;
      const input = inputRef.current;
      if (!input) return;
      const prefix = input.value && !input.value.endsWith(' ') ? ' ' : '';
      insertAtCursor(`${prefix}@${message.userName} `);
    };
    window.addEventListener('replyToUser', handleReply);
    return () => window.removeEventListener('replyToUser', handleReply);
  }, [insertAtCursor]);

  const sendMessage = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;
    const value = input.value.trim();
    if (!value) return;

    window.dispatchEvent(new CustomEvent('userMessage', {
      detail: { type: 'userMessage', value, roomId },
    }));

    input.value = '';
    resizeInput();
    setIsPickerOpen(false);
  }, [roomId, resizeInput]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handlePick = useCallback((id: number, type: PickerType) => {
    insertAtCursor(` <${type}/${id}> `);
  }, [insertAtCursor]);

  return (
    <div className={styles.chat}>
      <div className={styles.messages} ref={listRef}>
        {messages.length === 0 ? (
          <div className={styles.emptyState}>
            Napisz pierwszą wiadomość do rozmówcy 👋
          </div>
        ) : (
          messages.map((msg: IMessage, i: number) => (
            <Message key={`${i}-${msg.serverTime}`} message={msg} uniqueKey={`${i}-${msg.serverTime}`} />
          ))
        )}
      </div>

      {/* Picker emotek / gifów — wysuwany nad polem tekstowym */}
      <div className={classNames(styles.picker, { [styles.pickerOpen]: isPickerOpen })}>
        <div className={styles.pickerTabs}>
          <button
            type="button"
            className={classNames(styles.pickerTab, { [styles.pickerTabActive]: pickerTab === 'emoticons' })}
            onClick={() => setPickerTab('emoticons')}
          >
            Emotikony
          </button>
          <button
            type="button"
            className={classNames(styles.pickerTab, { [styles.pickerTabActive]: pickerTab === 'gifs' })}
            onClick={() => setPickerTab('gifs')}
          >
            Gify
          </button>
        </div>
        <div className={styles.pickerScroll}>
          <div style={{ display: pickerTab === 'emoticons' ? 'block' : 'none' }}>
            <PaginatedList
              items={emoticonsData as PickerItem[]}
              type="em"
              basePath="/img/emoticons/"
              onPick={handlePick}
              active={isPickerOpen && pickerTab === 'emoticons'}
            />
          </div>
          <div style={{ display: pickerTab === 'gifs' ? 'block' : 'none' }}>
            <PaginatedList
              items={Array.isArray(gifsData) ? (gifsData as PickerItem[]) : []}
              type="gif"
              basePath="/img/gifs/gifs_elements/"
              onPick={handlePick}
              active={isPickerOpen && pickerTab === 'gifs'}
            />
          </div>
        </div>
      </div>

      {/* Pasek wpisywania */}
      <div className={styles.composer}>
        <button
          type="button"
          className={classNames(styles.emojiBtn, { [styles.emojiBtnActive]: isPickerOpen })}
          onClick={() => setIsPickerOpen(v => !v)}
          aria-label="Emotikony"
          title="Emotikony"
        />
        <textarea
          ref={inputRef}
          className={styles.input}
          placeholder="Napisz wiadomość..."
          rows={1}
          maxLength={1520}
          autoComplete="off"
          onInput={resizeInput}
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          className={styles.sendBtn}
          onClick={sendMessage}
          aria-label="Wyślij"
          title="Wyślij"
        >
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
            <path
              d="M6.99811 10.2467L7.43298 11.0077C7.70983 11.4922 7.84825 11.7344 7.84825 12C7.84825 12.2656 7.70983 12.5078 7.43299 12.9923L6.99811 13.7533C5.75981 15.9203 5.14066 17.0039 5.62348 17.5412C6.1063 18.0785 7.24961 17.5783 9.53623 16.5779L15.8119 13.8323C17.6074 13.0468 18.5051 12.654 18.5051 12C18.5051 11.346 17.6074 10.9532 15.8119 10.1677L9.53624 7.4221C7.24962 6.42171 6.1063 5.92151 5.62348 6.45883C5.14066 6.99615 5.75981 8.07966 6.99811 10.2467Z"
              fill="currentColor"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default RouletteChat;
